import React from "react";
import { Store, MapPin } from "lucide-react";

interface BizNearYouLogoProps {
  className?: string;
  iconOnly?: boolean;
  textColor?: string;
  isDark?: boolean;
}

export default function BizNearYouLogo({ className = "", iconOnly = false, textColor = "text-slate-900", isDark = true }: BizNearYouLogoProps) {
  // Determine if we are overriding with a specific high-contrast color (like in dark header or dark background screens)
  const isForcedLight = textColor.includes("text-white") || textColor.includes("text-slate-100");
  
  // "Biz" color: use the passed textColor directly to support different themes!
  const bizColor = textColor || (isForcedLight 
    ? "text-white" 
    : isDark 
    ? "text-slate-50" 
    : "text-slate-900");

  // "NearYou" colors: beautiful teal & soft green gradient (100% static and brand-consistent across all themes!)
  const nearYouGradient = "from-teal-500 via-cyan-500 to-emerald-400";

  return (
    <div className={`flex items-center gap-3 select-none ${className}`}>
      {/* Target element for style & icon changes */}
      <div className="relative flex items-center justify-center w-9 h-9 shrink-0" id="header-logo-icon">
        {/* Beautiful concentric background radar rings matching the provided image */}
        <div className="absolute -inset-1 border border-teal-500/20 rounded-full pointer-events-none animate-pulse"></div>
        <div className="absolute -inset-2.5 border border-teal-500/10 rounded-full pointer-events-none"></div>
        <div className="absolute -inset-4 border border-teal-500/5 rounded-full pointer-events-none"></div>

        {/* 100% Vector, Premium Map Pin Brand Icon that is static across all themes */}
        <svg 
          viewBox="0 0 100 100" 
          className="w-9 h-9 select-none filter drop-shadow-[0_2px_4px_rgba(13,148,136,0.2)]"
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Glossy gradient for the Map Pin body */}
            <linearGradient id="logoPinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#14b8a6" /> {/* teal-500 */}
              <stop offset="40%" stopColor="#06b6d4" /> {/* cyan-500 */}
              <stop offset="100%" stopColor="#0d9488" /> {/* teal-600 */}
            </linearGradient>
            
            {/* Disc inside the pin head */}
            <linearGradient id="logoDiscGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e0f2fe" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>

            {/* Awning stripe clip */}
            <clipPath id="logoAwningClip">
              <path d="M34 38 L38 32 H62 L66 38 Z" />
            </clipPath>
          </defs>

          {/* Pin Shadow */}
          <ellipse cx="50" cy="85" rx="8" ry="2.5" fill="#0d9488" opacity="0.3" />

          {/* Classic Map Pin Shape */}
          <path 
            d="M50 86C47.5 86 24 58 24 42C24 27.64 35.64 16 50 16C64.36 16 76 27.64 76 42C76 58 52.5 86 50 86Z" 
            fill="url(#logoPinGrad)" 
          />

          {/* Inner Storefront Circular Disc */}
          <circle cx="50" cy="42" r="17" fill="url(#logoDiscGrad)" />

          {/* Building wall inside storefront disc */}
          <rect x="38" y="42" width="24" height="11" fill="#0d9488" opacity="0.08" />
          <path d="M40 42 V52 H60 V42" stroke="#0f766e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Awning/Canopy Base */}
          <path d="M34 38 L38 32 H62 L66 38 Z" fill="#0d9488" />
          
          {/* Awning Stripes */}
          <g clipPath="url(#logoAwningClip)">
            <rect x="34" y="32" width="32" height="6" fill="#0d9488" />
            <rect x="40" y="32" width="4.5" height="6" fill="#22d3ee" />
            <rect x="49" y="32" width="4.5" height="6" fill="#22d3ee" />
            <rect x="58" y="32" width="4.5" height="6" fill="#22d3ee" />
          </g>

          {/* Awning Scallops at the bottom of awning */}
          <path 
            d="M34 38 Q36.6 40.5 39.3 38 Q42 40.5 44.6 38 Q47.3 40.5 50 38 Q52.6 40.5 55.3 38 Q58 40.5 60.6 38 Q63.3 40.5 66 38" 
            fill="none" 
            stroke="#0d9488" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
          />

          {/* Shop Door */}
          <rect x="46" y="46" width="8" height="6" fill="#0f766e" rx="1" />
          <rect x="47.5" y="47.5" width="5" height="4.5" fill="#a7f3d0" rx="0.5" />
        </svg>
      </div>
      {!iconOnly && (
        <span className="font-outfit text-2xl tracking-tight flex items-center select-none" id="brand-wordmark">
          {/* "Biz" is slightly bolder/more energetic */}
          <span className={`font-black ${bizColor} flex items-center`}>
            Biz
          </span>
          {/* "NearYou" is smooth and trustworthy with a teal-to-emerald gradient */}
          <span className={`font-medium bg-gradient-to-r ${nearYouGradient} bg-clip-text text-transparent ml-0.5 tracking-tight flex items-center`}>
            NearYou
            {/* Tiny elegant location pin beacon dot to balance the logo layout */}
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 ml-1 shadow-xs shadow-teal-500/40"></span>
          </span>
        </span>
      )}
    </div>
  );
}
